/**
 * Price parsing and formatting helpers shared by the comparison views.
 *
 * Pure functions only — kept apart from the cell components in
 * comparisonCells.jsx so each file satisfies react-refresh's
 * one-kind-of-export rule.
 */

/**
 * Parse a price value (string or number) to a float.
 *
 * @param {string|number|null|undefined} val
 * @returns {number|null}
 */
export function parsePrice(val) {
  if (val == null) return null;
  const n = typeof val === 'string' ? parseFloat(val) : val;
  return Number.isFinite(n) ? n : null;
}

/**
 * Format a numeric price as "\u00a3XX.XX".
 *
 * @param {number|null} val
 * @returns {string}
 */
export function formatPrice(val) {
  if (val == null) return '';
  return `\u00a3${val.toFixed(2)}`;
}

/**
 * Format a difference percentage with sign (e.g. "+12.3%" or "-5.1%").
 *
 * @param {number|null} pct
 * @returns {string}
 */
export function formatDiffPct(pct) {
  if (pct == null) return '';
  const sign = pct > 0 ? '+' : '';
  return `${sign}${pct.toFixed(1)}%`;
}

/**
 * Calculate the percentage difference of a competitor price vs our price.
 *
 * @param {number|null} compPrice
 * @param {number|null} ourPrice
 * @returns {number|null}
 */
export function calcDiffPct(compPrice, ourPrice) {
  if (compPrice == null || ourPrice == null || ourPrice === 0) return null;
  return ((compPrice - ourPrice) / ourPrice) * 100;
}

/**
 * Classify our price against the cheapest in-stock competitor price
 * (the "market floor") under the follow-the-market pricing policy.
 *
 * States (rev 3, sponsor-approved 2026-08-25: red is the single scan
 * signal — every red row is a product we are undercutting on):
 *   - "match" — equal to the floor (green "=")
 *   - "above" — above the floor (green "▲"; the glyph and the "vs min"
 *     percentage keep it tellable apart from "match" without colour)
 *   - "under" — below every in-stock competitor (red "▼")
 *
 * Equality is exact to the penny, compared in integer pence to avoid
 * float noise — the same rule as the "We Match the Cheapest" dashboard
 * KPI, so the table and the KPI can never disagree.
 *
 * @param {number|null} ourVal Our price.
 * @param {number|null} minVal Cheapest in-stock competitor price.
 * @returns {{state: string, colorClass: string, glyph: string, diffPct: number|null}|null}
 *   Display props, or null when there is no market floor to compare
 *   against (no in-stock competitor price) — render neutral black.
 */
export function ourPricePosition(ourVal, minVal) {
  if (ourVal == null || minVal == null) return null;
  const ourPence = Math.round(ourVal * 100);
  const minPence = Math.round(minVal * 100);
  if (ourPence === minPence) {
    return { state: 'match', colorClass: 'text-green-700', glyph: '=', diffPct: null };
  }
  const diffPct = ((ourVal - minVal) / minVal) * 100;
  if (ourPence < minPence) {
    return { state: 'under', colorClass: 'text-red-600', glyph: '▼', diffPct };
  }
  return { state: 'above', colorClass: 'text-green-700', glyph: '▲', diffPct };
}
